// src/components/UserPermissionsTab.jsx
import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { Shield, Building, SlidersHorizontal, Pencil, Check, X as Cerrar } from 'lucide-react';
import { Users, ChevronDown, ChevronUp, RefreshCw, Eye, EyeOff, Plus } from './icons';
import { usuariosAPI } from '../services/api';
import { useToast } from '../contexts/ToastContext';
import PermisosFinosApp from './PermisosFinosApp';
import LogoApp from './LogoApp';
import { aplicacionesAPI } from '../services/api';
import { normalizarEmpresa } from '../utils/empresaUtils';

// Apps con perfil Staff/Usuario y cuentas locales (usuario+contraseña, sin correo).
const APPS_CON_PERFIL_Y_CUENTAS = ['exportaciones', 'docs', 'devoluciones'];
// Apps con un perfil global (fila general con Scope) pero sin cuentas locales: el acceso y el perfil viven en el login central
const APPS_CON_PERFIL_SIN_CUENTAS = ['reclamos'];

// Perfiles disponibles por app (por defecto Staff y Usuario). En Devoluciones también hay Admin; Staff puede manejar usuarios.
const PERFILES_POR_APP = { devoluciones: ['staff', 'admin', 'usuario'], reclamos: ['usuario', 'staff', 'admin'] };
const PERFILES_DEFECTO = ['staff', 'usuario'];
const perfilesDe = (codigo) => PERFILES_POR_APP[codigo] || PERFILES_DEFECTO;
const etiquetaPerfil = (p) => ({ staff: 'Staff', admin: 'Admin', usuario: 'Usuario', avanzado: 'Avanzado' }[p] || p);

// Apps con permisos granulares por persona (catalogo propio en el backend)
const APPS_CON_PERMISOS_FINOS = ['muestras', 'muestras-vicsa', 'apro'];

// La app se llamaba «tracking» y pasa a «spytrack»: se aceptan ambos códigos mientras dure el cambio
const CODIGOS_TRACKING = ['tracking', 'spytrack'];

// Tracking: el acceso se da por empresa (Vicsa, Tecnoboga, B2B) y el Scope de cada fila es el perfil en esa empresa.
const EMPRESAS_TRACKING = ['VIC', 'TEC', 'B2B'];
// Empresa de la ficha de la persona -> código de empresa en tracking
const EMPRESA_A_CODIGO_TRACKING = { Vicsa: 'VIC', Tecnoboga: 'TEC', B2B: 'B2B' };
const CAPACIDADES_POR_DEFECTO = {
  admin: 'Todo, incluida la gestión de usuarios de la app',
  avanzado: 'Todo menos la gestión de usuarios',
  staff: 'Solo buscar OV y OC',
};
const PERFILES_TRACKING = [
  { clave: 'admin', etiqueta: 'Admin' },
  { clave: 'avanzado', etiqueta: 'Avanzado' },
  { clave: 'staff', etiqueta: 'Staff' },
];

// Clases compartidas por las ventanas de detalle y edición
const botonSecundario = 'inline-flex items-center gap-1.5 px-3 py-2 text-xs font-semibold rounded-lg bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-300 transition-colors';
const tituloSeccion = 'text-xs font-bold uppercase tracking-wider text-gray-600 dark:text-gray-300 flex items-center mb-2';
const etiquetaCampo = 'block text-[11px] font-semibold text-gray-500 dark:text-gray-400 mb-1';
const campo = 'w-full px-3 py-2 text-sm rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500';

const claseAcceso = (r) => (r.tieneGeneral
  ? 'bg-green-100 dark:bg-green-900/40 text-green-700 dark:text-green-300'
  : r.tieneAlgun
    ? 'bg-blue-100 dark:bg-blue-900/40 text-blue-700 dark:text-blue-300'
    : 'bg-gray-100 dark:bg-gray-700 text-gray-500 dark:text-gray-400');

function Modal({ logo, titulo, subtitulo, onClose, children, ancho = 'max-w-2xl', z = 9000, esc = true }) {
  useEffect(() => {
    if (!esc) return undefined;
    const alTeclear = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', alTeclear);
    return () => document.removeEventListener('keydown', alTeclear);
  }, [esc, onClose]);

  return createPortal(
    <div role="dialog" aria-modal="true" className="fixed inset-0 overflow-y-auto bg-black/50" style={{ zIndex: z }}>
      <div className="min-h-full flex items-start justify-center p-3 sm:p-6" onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}>
        <div className={`w-full ${ancho} my-2 bg-white dark:bg-gray-800 rounded-2xl shadow-2xl border border-gray-200 dark:border-gray-700`}>
          <div className="flex items-start justify-between gap-4 p-5 border-b border-gray-100 dark:border-gray-700">
            <div className="min-w-0 flex items-center gap-3">
              {logo}
              <div className="min-w-0">
                <h3 className="text-lg font-bold text-gray-900 dark:text-white">{titulo}</h3>
                {subtitulo && <p className="text-sm text-gray-500 dark:text-gray-400 mt-0.5">{subtitulo}</p>}
              </div>
            </div>
            <button type="button" onClick={onClose} aria-label="Cerrar" className="p-1.5 rounded-lg text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 shrink-0">
              <Cerrar className="w-5 h-5" />
            </button>
          </div>
          <div className="p-5">{children}</div>
        </div>
      </div>
    </div>,
    document.body,
  );
}

function Interruptor({ activo, onChange, disabled, etiqueta }) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={activo}
      aria-label={etiqueta}
      disabled={disabled}
      onClick={(e) => { e.stopPropagation(); onChange(); }}
      className={`relative inline-flex h-6 w-11 shrink-0 items-center rounded-full transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2 disabled:opacity-50 ${activo ? 'bg-blue-600' : 'bg-gray-300 dark:bg-gray-600'}`}
    >
      <span className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform ${activo ? 'translate-x-6' : 'translate-x-1'}`} />
    </button>
  );
}

// Contraseña temporal legible (evita 0/O, 1/l/I) -- se muestra una sola vez
// al crear la cuenta o al restablecerla, para copiarla y pasarla a la persona.
function generarPasswordTemporal() {
  const chars = 'ABCDEFGHJKMNPQRSTUVWXYZabcdefghjkmnpqrstuvwxyz23456789';
  let out = '';
  for (let i = 0; i < 12; i++) out += chars[Math.floor(Math.random() * chars.length)];
  return out;
}

export default function UserPermissionsTab({ userId, userEmail, isActive, userNombre, userApellido, userUsername, userEmpresa }) {
  const { showToast } = useToast();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [catalogo, setCatalogo] = useState({ apps: [], empresas: [], clientes: [], relaciones: [] });
  const [permisos, setPermisos] = useState([]);
  const [appDetalle, setAppDetalle] = useState(null); // id de la app abierta en la ventana de detalle
  const [editor, setEditor] = useState(null); // ventana de edición: { tipo, appId | appCodigo, cuentaId }
  const [confirmarEliminar, setConfirmarEliminar] = useState(false);
  const [seccion, setSeccion] = useState(null); // sección desplegada en la ventana de detalle
  const [matrices, setMatrices] = useState({}); // perfiles y capacidades por app (los define api_vicsa)

  // --- Cuentas locales (usuario + contraseña) para apps sin correo
  // corporativo, ej. Exportaciones. Se cargan aparte porque son otra tabla
  // (dbo.API_USUARIOS con AuthMethod local/ambos), no un permiso mas. ---
  const [cuentasPorApp, setCuentasPorApp] = useState({});
  const [loadingCuentas, setLoadingCuentas] = useState(false);
  const [formCrear, setFormCrear] = useState({ nombre: '', apellido: '', usuario: '', password: '', scope: 'usuario', mostrarPassword: false });
  const [savingCuenta, setSavingCuenta] = useState(false);
  const [resetPassword, setResetPassword] = useState({ password: '', mostrarPassword: false });

  // `silent` evita el spinner de pantalla completa -- se usa al refrescar tras
  // tocar un checkbox/botón para que el panel no se desmonte y la página no
  // salte de posición; solo se usa el spinner grande en la carga inicial.
  const cargarPermisos = async (silent = false) => {
    try {
      if (!silent) setLoading(true);
      const [resCat, resPerm] = await Promise.all([
        usuariosAPI.getPermisosCatalogo(),
        usuariosAPI.getPermisosUsuario(userId)
      ]);
      const cat = resCat.data || { apps: [], empresas: [], clientes: [], relaciones: [] };
      // «Tracking» pasó a llamarse SpyTrack
      setCatalogo({ ...cat, apps: (cat.apps || []).map((a) => (a.codigo === 'tracking' ? { ...a, nombre: 'SpyTrack' } : a)) });
      setPermisos(resPerm.data || []);
    } catch (err) {
      console.error('Error al cargar permisos:', err);
      showToast('Error', 'No se pudieron cargar los permisos del usuario', 'error');
    } finally {
      if (!silent) setLoading(false);
    }
  };

  useEffect(() => {
    if (userId) {
      cargarPermisos();
    }
  }, [userId]);

  // Cuentas locales de Exportaciones: se traen una vez que se sabe que esa
  // app existe en el catálogo (no todas las instalaciones la tendrán).
  useEffect(() => {
    catalogo.apps
      .filter((a) => APPS_CON_PERFIL_Y_CUENTAS.includes(a.codigo))
      .forEach((a) => cargarCuentasLocales(a.codigo));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [catalogo.apps]);

  // Perfiles y capacidades de SpyTrack/Tracking: se cargan al abrir la app
  useEffect(() => {
    const codigo = catalogo.apps.find((a) => a.id === appDetalle)?.codigo;
    if (!codigo || !(CODIGOS_TRACKING.includes(codigo) || APPS_CON_PERFIL_SIN_CUENTAS.includes(codigo)) || matrices[codigo]) return;
    aplicacionesAPI.getPerfiles(codigo)
      .then((r) => setMatrices((m) => ({ ...m, [codigo]: r.data })))
      .catch(() => setMatrices((m) => ({ ...m, [codigo]: { roles: [], permisos: [] } })));
  }, [appDetalle, catalogo.apps, matrices]);

  if (loading) {
    return (
      <div className="p-8 text-center bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700">
        <RefreshCw className="w-8 h-8 animate-spin text-blue-500 mx-auto mb-3" />
        <p className="text-gray-500 dark:text-gray-400 font-medium">Cargando aplicaciones y permisos granulares...</p>
      </div>
    );
  }

  // Verifica si el usuario tiene permiso para una app (acceso general o específico)
  const tienePermisoApp = (appId) => {
    return permisos.some(p => p.appId === appId && p.activo);
  };

  // Verifica si tiene acceso total a una app (empresaId=NULL y clienteId=NULL)
  const tieneAccesoTotal = (appId) => {
    return permisos.some(p => p.appId === appId && !p.empresaId && !p.clienteId && p.activo);
  };

  // Busca el permiso específico de una combinación App + Empresa + Cliente
  const getPermisoEspecifico = (appId, empresaId, clienteId) => {
    return permisos.find(p => p.appId === appId && p.empresaId === empresaId && p.clienteId === clienteId && p.activo);
  };

  // Scope actual del permiso general de una app (ej. 'staff'/'usuario' en Exportaciones).
  // null si todavia no tiene acceso otorgado -- ahi no se resalta ningun botón.
  const getScopeApp = (appId) => {
    const p = permisos.find(perm => perm.appId === appId && !perm.empresaId && !perm.clienteId && perm.activo);
    return p?.scope || null;
  };

  const cambiarScope = async (appId, scope) => {
    try {
      setSaving(true);
      await usuariosAPI.otorgarPermiso(userId, { appId, empresaId: null, clienteId: null, scope });
      showToast('Éxito', `Perfil actualizado a ${etiquetaPerfil(scope)}`, 'success');
      await cargarPermisos(true);
    } catch (err) {
      console.error('Error al cambiar el scope:', err);
      showToast('Error', 'No se pudo actualizar el perfil', 'error');
    } finally {
      setSaving(false);
    }
  };

  // Perfil (staff/usuario) de una app con permisos granulares (Autogestion): vive en
  // el Scope de las filas por empresa/cliente, no en una fila general.
  const getScopeGranular = (appId) => {
    const p = permisos.find((perm) => perm.appId === appId && perm.activo && perm.scope);
    return p?.scope || null;
  };

  const cambiarScopeGranular = async (appId, scope) => {
    try {
      setSaving(true);
      await usuariosAPI.cambiarScopeApp(userId, { appId, scope });
      showToast('Éxito', `Perfil actualizado a ${scope === 'staff' ? 'Staff' : 'Usuario'}`, 'success');
      await cargarPermisos(true);
    } catch (err) {
      console.error('Error al cambiar el perfil:', err);
      showToast('Error', err.response?.data?.message || 'No se pudo actualizar el perfil', 'error');
    } finally {
      setSaving(false);
    }
  };

  // Trae las cuentas locales (usuario+contraseña) de una app -- se llama al
  // expandir la tarjeta de Exportaciones, no en la carga inicial (no todas
  // las apps la necesitan).
  const cargarCuentasLocales = async (appCodigo) => {
    try {
      setLoadingCuentas(true);
      const { data } = await usuariosAPI.listarCuentasLocalesApp(appCodigo);
      setCuentasPorApp((prev) => ({ ...prev, [appCodigo]: Array.isArray(data) ? data : [] }));
    } catch (err) {
      console.error('Error al cargar cuentas locales:', err);
      showToast('Error', 'No se pudieron cargar las cuentas locales', 'error');
      setCuentasPorApp((prev) => ({ ...prev, [appCodigo]: [] }));
    } finally {
      setLoadingCuentas(false);
    }
  };

  const handleCrearCuentaLocal = async (appCodigo) => {
    const usaCorreo = (catalogo.apps.find((a) => a.codigo === appCodigo)?.metodoSegundoFactor || '').toLowerCase() === 'correo';
    if (!formCrear.nombre.trim() || !formCrear.apellido.trim() || !formCrear.usuario.trim() || !formCrear.password.trim()) {
      showToast('Error', usaCorreo ? 'Completa nombre, apellido, correo y contraseña' : 'Completa nombre, apellido, usuario y contraseña', 'error');
      return;
    }
    if (usaCorreo && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formCrear.usuario.trim())) {
      showToast('Error', 'Ingresa un correo válido: ahí llegará el código del doble factor', 'error');
      return;
    }
    try {
      setSavingCuenta(true);
      await usuariosAPI.crearCuentaLocalApp(appCodigo, {
        nombre: formCrear.nombre.trim(),
        apellido: formCrear.apellido.trim(),
        usuario: formCrear.usuario.trim(),
        ...(usaCorreo ? { correo: formCrear.usuario.trim() } : {}),
        password: formCrear.password,
        scope: formCrear.scope || 'usuario',
      });
      showToast('Éxito', 'Cuenta local creada. Comparte el usuario y la contraseña con la persona.', 'success');
      setEditor(null);
      setFormCrear({ nombre: '', apellido: '', usuario: '', password: '', scope: 'usuario', mostrarPassword: false });
      await cargarCuentasLocales(appCodigo);
    } catch (err) {
      console.error('Error al crear cuenta local:', err);
      showToast('Error', err.response?.data?.message || 'No se pudo crear la cuenta local', 'error');
    } finally {
      setSavingCuenta(false);
    }
  };

  const handleEliminarCuentaLocal = async (appCodigo, apiUserId) => {
    try {
      setSavingCuenta(true);
      await usuariosAPI.eliminarCuentaLocal(apiUserId);
      showToast('Éxito', 'Cuenta local eliminada', 'success');
      setEditor(null);
      await cargarCuentasLocales(appCodigo);
    } catch (err) {
      console.error('Error al eliminar cuenta local:', err);
      showToast('Error', err.response?.data?.message || 'No se pudo eliminar la cuenta local', 'error');
    } finally {
      setSavingCuenta(false);
    }
  };

  const handleResetPassword = async (appCodigo, apiUserId) => {
    if (!resetPassword.password.trim()) {
      showToast('Error', 'Ingresa la nueva contraseña', 'error');
      return;
    }
    try {
      setSavingCuenta(true);
      await usuariosAPI.resetPasswordCuentaLocal(apiUserId, resetPassword.password);
      showToast('Éxito', 'Contraseña actualizada. Compártela con la persona.', 'success');
      setEditor(null);
      setResetPassword({ password: '', mostrarPassword: false });
      await cargarCuentasLocales(appCodigo);
    } catch (err) {
      console.error('Error al restablecer contraseña:', err);
      showToast('Error', err.response?.data?.message || 'No se pudo actualizar la contraseña', 'error');
    } finally {
      setSavingCuenta(false);
    }
  };

  const handleToggleActivoCuenta = async (appCodigo, apiUserId, activo) => {
    try {
      setSavingCuenta(true);
      await usuariosAPI.toggleActivoCuentaLocal(apiUserId, !activo);
      showToast('Éxito', !activo ? 'Cuenta activada' : 'Cuenta desactivada', 'success');
      await cargarCuentasLocales(appCodigo);
    } catch (err) {
      console.error('Error al cambiar estado de la cuenta:', err);
      showToast('Error', 'No se pudo actualizar la cuenta', 'error');
    } finally {
      setSavingCuenta(false);
    }
  };

  // Quita el código repetido entre paréntesis al final del nombre (ej. "APRO LTDA (APRO)" -> "APRO LTDA")
  const limpiarNombreCliente = (nombre, codigo) => {
    if (!nombre || !codigo) return nombre;
    const patron = new RegExp(`\\s*\\(${codigo.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}\\)\\s*$`, 'i');
    return nombre.replace(patron, '').trim();
  };

  // Tracking: fila por empresa (sin cliente) de la app
  const getPermisoEmpresa = (appId, empresaId) => permisos.find((p) => p.appId === appId && p.empresaId === empresaId && !p.clienteId && p.activo);

  const empresaPropiaCodigo = EMPRESA_A_CODIGO_TRACKING[normalizarEmpresa(userEmpresa)] || null;

  // Interruptor de acceso a tracking/SpyTrack: ENCENDER da acceso en la empresa a la que pertenece la persona (perfil Staff, el más
  // acotado; se sube después); APAGAR quita el acceso en todas las empresas. El perfil nunca se da «general»: siempre por empresa.
  const toggleAccesoTracking = async (app) => {
    const filas = permisos.filter((p) => p.appId === app.id && p.activo);
    try {
      setSaving(true);
      if (filas.length > 0) {
        for (const f of filas) await usuariosAPI.revocarPermiso(f.id);
        showToast('Éxito', `Acceso a ${app.nombre} quitado en todas las empresas`, 'success');
      } else {
        const emp = catalogo.empresas.find((e) => String(e.codigo).toUpperCase() === empresaPropiaCodigo);
        if (!emp) {
          showToast('Error', 'No se pudo deducir la empresa de esta persona: elígela abajo en «Empresas y perfil».', 'error');
          setSeccion('empresas-tracking');
          return;
        }
        await usuariosAPI.otorgarPermiso(userId, { appId: app.id, empresaId: emp.id, clienteId: null, scope: 'staff' });
        showToast('Éxito', `Acceso a ${app.nombre} en ${emp.nombre} con perfil Staff`, 'success');
      }
      await cargarPermisos(true);
    } catch (err) {
      console.error('Error al cambiar el acceso a tracking:', err);
      showToast('Error', err.response?.data?.message || 'No se pudo cambiar el acceso', 'error');
    } finally {
      setSaving(false);
    }
  };

  // Clic en una empresa bajo un perfil: si ya tiene ESE perfil en la empresa, se quita; si no, se le asigna (reemplaza el que tuviera)
  const asignarPerfilEmpresa = async (app, emp, perfil) => {
    try {
      setSaving(true);
      const actual = getPermisoEmpresa(app.id, emp.id);
      if (actual && actual.scope === perfil) {
        await usuariosAPI.revocarPermiso(actual.id);
        showToast('Éxito', `Acceso a ${emp.nombre} quitado`, 'success');
      } else {
        const general = permisos.find((p) => p.appId === app.id && !p.empresaId && !p.clienteId && p.activo);
        if (general) await usuariosAPI.revocarPermiso(general.id);
        await usuariosAPI.otorgarPermiso(userId, { appId: app.id, empresaId: emp.id, clienteId: null, scope: perfil });
        showToast('Éxito', `${emp.nombre}: perfil ${etiquetaPerfil(perfil)}`, 'success');
      }
      await cargarPermisos(true);
    } catch (err) {
      console.error('Error al cambiar el perfil por empresa:', err);
      showToast('Error', err.response?.data?.message || 'No se pudo actualizar el acceso a la empresa', 'error');
    } finally {
      setSaving(false);
    }
  };

  // Habilitar o Deshabilitar App completa (Acceso Total)
  const toggleAccesoGeneralApp = async (appId) => {
    try {
      setSaving(true);
      const generalPerm = permisos.find(p => p.appId === appId && !p.empresaId && !p.clienteId && p.activo);

      if (generalPerm) {
        // Revocar acceso general
        await usuariosAPI.revocarPermiso(generalPerm.id);
        showToast('Éxito', 'Acceso general revocado', 'success');
      } else {
        // Otorgar acceso general
        await usuariosAPI.otorgarPermiso(userId, { appId, empresaId: null, clienteId: null });
        showToast('Éxito', 'Acceso general a la aplicación otorgado', 'success');
      }
      await cargarPermisos(true);
    } catch (err) {
      console.error('Error al cambiar acceso general:', err);
      showToast('Error', 'No se pudo actualizar el permiso', 'error');
    } finally {
      setSaving(false);
    }
  };

  // Habilitar o Deshabilitar un permiso específico (Empresa + Cliente)
  const togglePermisoGranular = async (appId, empresaId, clienteId) => {
    try {
      setSaving(true);
      const perm = getPermisoEspecifico(appId, empresaId, clienteId);

      if (perm) {
        await usuariosAPI.revocarPermiso(perm.id);
        showToast('Éxito', 'Permiso específico revocado', 'success');
      } else {
        await usuariosAPI.otorgarPermiso(userId, { appId, empresaId, clienteId });
        showToast('Éxito', 'Permiso específico otorgado', 'success');
      }
      await cargarPermisos(true);
    } catch (err) {
      console.error('Error al cambiar permiso granular:', err);
      showToast('Error', 'No se pudo actualizar el permiso específico', 'error');
    } finally {
      setSaving(false);
    }
  };

  // Resumen de una app para la tarjeta y las ventanas: acceso, perfil y datos relevantes
  const resumenApp = (app) => {
    const tieneGeneral = tieneAccesoTotal(app.id);
    const tieneAlgun = tienePermisoApp(app.id);
    const esAutogestion = app.codigo === 'autogestion' || app.codigo === 'homologacion';
    const esTracking = CODIGOS_TRACKING.includes(app.codigo);
    const empresasTracking = esTracking ? permisos.filter((p) => p.appId === app.id && p.activo && p.empresaId && !p.clienteId) : [];
    const esExportaciones = APPS_CON_PERFIL_Y_CUENTAS.includes(app.codigo);
    const tienePermisosFinos = APPS_CON_PERMISOS_FINOS.includes(app.codigo);
    const usaCorreoDoble = (app.metodoSegundoFactor || '').toLowerCase() === 'correo';
    const perfilGeneral = esExportaciones || APPS_CON_PERFIL_SIN_CUENTAS.includes(app.codigo);
    const tienePerfiles = perfilGeneral || app.codigo === 'autogestion';
    const perfiles = perfilGeneral ? perfilesDe(app.codigo) : ['staff', 'usuario'];
    let perfilClave = null;
    let perfilPorDefecto = false;
    if (perfilGeneral) {
      const guardado = getScopeApp(app.id);
      perfilPorDefecto = tieneGeneral && !guardado;
      perfilClave = guardado || (perfilPorDefecto ? 'usuario' : null);
    } else if (app.codigo === 'autogestion' && tieneAlgun) {
      const guardado = getScopeGranular(app.id);
      perfilPorDefecto = !guardado;
      perfilClave = guardado || 'usuario';
    }
    const perfilTexto = perfilClave ? `${etiquetaPerfil(perfilClave)}${perfilPorDefecto ? ' (por defecto)' : ''}` : null;
    const combinaciones = permisos.filter((p) => p.appId === app.id && p.activo && p.empresaId && p.clienteId).length;
    const datos = [];
    if (perfilTexto) datos.push(['Perfil', perfilTexto]);
    if (esExportaciones) datos.push(['Cuentas locales', cuentasPorApp[app.codigo] ? cuentasPorApp[app.codigo].length : '…']);
    if (esAutogestion) datos.push(['Empresas y clientes', tieneGeneral ? 'Todos' : combinaciones]);
    if (esTracking) datos.push(['Empresas', empresasTracking.length ? empresasTracking.map((p) => p.empresaCodigo).filter(Boolean).join(' · ') : tieneGeneral ? 'Todas las de su ficha' : 'Ninguna']);
    if (tienePermisosFinos) datos.push(['Rol y permisos', 'Configurables']);
    if (app.metodoSegundoFactor) datos.push(['Doble factor', usaCorreoDoble ? 'Correo' : 'Autenticador']);
    if (!datos.length) datos.push(['Acceso', tieneGeneral ? 'A toda la app' : tieneAlgun ? 'Parcial' : 'No asignado']);
    return {
      tieneGeneral, tieneAlgun, esAutogestion, esTracking, empresasTracking, esExportaciones, perfilGeneral, tienePermisosFinos, usaCorreoDoble,
      tienePerfiles, perfiles, perfilClave, perfilPorDefecto, perfilTexto, datos,
      etiquetaAcceso: esTracking && empresasTracking.length ? 'Acceso por empresa' : tieneGeneral ? 'Acceso Total' : tieneAlgun ? 'Acceso Granular' : 'Sin Acceso',
    };
  };
  const appActual = catalogo.apps.find((a) => a.id === appDetalle) || null;

  // Secciones desplegables dentro de la ventana de detalle (se editan ahí mismo)
  const seccionAcceso = (app, r) => (
    <>
              <div className="space-y-5">
                {r.tienePerfiles && (r.perfilGeneral ? true : r.tieneAlgun) && (
                  <div>
                    <p className="text-sm font-semibold text-gray-800 dark:text-gray-200 mb-1.5">Perfil</p>
                    <div className="inline-flex items-center rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
                      {r.perfiles.map((perfil, i) => (
                        <button
                          key={perfil}
                          type="button"
                          disabled={saving || (r.perfilGeneral && !r.tieneGeneral)}
                          onClick={() => (r.perfilGeneral ? cambiarScope(app.id, perfil) : cambiarScopeGranular(app.id, perfil))}
                          className={`px-4 py-2 text-xs font-bold transition-colors disabled:opacity-50 ${i > 0 ? 'border-l border-gray-200 dark:border-gray-700' : ''} ${
                            r.perfilClave === perfil ? 'bg-blue-600 text-white' : 'bg-gray-50 dark:bg-gray-900 text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800'
                          }`}
                        >
                          {etiquetaPerfil(perfil)}
                        </button>
                      ))}
                    </div>
                    {r.perfilGeneral && !r.tieneGeneral && <p className="text-xs text-gray-500 mt-1.5">Activa primero el acceso para elegir un perfil.</p>}
                    {r.perfilPorDefecto && <p className="text-xs text-gray-500 mt-1.5">Mientras no elijas uno, la app lo trata como Usuario.</p>}
                  </div>
                )}
                {APPS_CON_PERFIL_SIN_CUENTAS.includes(app.codigo) && (tablaCapacidades(matrices[app.codigo]) || <p className="text-xs text-gray-500">Cargando perfiles…</p>)}
              </div>
    </>
  );
  const seccionEmpresas = (app, r) => (
    <>
              {r.tieneGeneral && <p className="mb-3 text-xs text-amber-700 dark:text-amber-400">Tiene acceso total: todas las combinaciones están incluidas. Revócalo en «Editar acceso» para elegir algunas.</p>}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {catalogo.empresas.map((emp) => {
                  const relsEmpresa = catalogo.relaciones.filter((x) => x.empresaId === emp.id);
                  return (
                    <div key={emp.id} className="bg-white dark:bg-gray-800 p-4 rounded-xl border border-gray-200 dark:border-gray-700">
                      <div className="flex items-center gap-2 mb-3 pb-2 border-b border-gray-100 dark:border-gray-700">
                        <span className="font-mono font-bold text-xs px-2 py-0.5 bg-blue-100 dark:bg-blue-900/50 text-blue-700 dark:text-blue-300 rounded">{emp.codigo}</span>
                        <span className="font-semibold text-sm text-gray-900 dark:text-white">{emp.nombre}</span>
                      </div>
                      {relsEmpresa.length === 0 ? (
                        <p className="text-xs text-gray-400 italic py-2">Sin clientes asignados a esta empresa</p>
                      ) : (
                        <div className="space-y-2">
                          {relsEmpresa.map((rel) => {
                            const permisoEsp = getPermisoEspecifico(app.id, emp.id, rel.clienteId);
                            const marcado = Boolean(permisoEsp) || r.tieneGeneral;
                            return (
                              <label key={rel.id} className={`flex items-center justify-between p-2.5 rounded-lg border text-xs cursor-pointer transition-all ${
                                marcado ? 'bg-blue-50/70 dark:bg-blue-950/30 border-blue-200 dark:border-blue-800 text-blue-900 dark:text-blue-200 font-medium' : 'bg-gray-50 dark:bg-gray-900/50 border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-400 hover:bg-gray-100'
                              } ${r.tieneGeneral ? 'opacity-70 cursor-not-allowed' : ''}`}>
                                <div className="flex items-center gap-2.5">
                                  <input type="checkbox" disabled={saving || r.tieneGeneral} checked={marcado} onChange={() => togglePermisoGranular(app.id, emp.id, rel.clienteId)} className="w-4 h-4 text-blue-600 rounded border-gray-300 focus:ring-blue-500 dark:border-gray-600 dark:bg-gray-700" />
                                  <span>{limpiarNombreCliente(rel.clienteNombre, rel.clienteCodigo)}</span>
                                </div>
                                {permisoEsp && <span className="text-[10px] px-1.5 py-0.5 bg-blue-200 dark:bg-blue-800 text-blue-800 dark:text-blue-200 rounded font-bold">Asignado</span>}
                              </label>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
    </>
  );
  // Tabla «qué puede hacer cada perfil» (la define api_vicsa; Reclamos y SpyTrack la comparten)
  const tablaCapacidades = (matriz) => matriz?.roles?.length > 0 && (
          <div className="mt-5">
            <p className="text-xs font-bold uppercase tracking-wider text-gray-600 dark:text-gray-300 mb-2">Qué puede hacer cada perfil</p>
            <div className="overflow-x-auto rounded-lg border border-gray-200 dark:border-gray-700">
              <table className="w-full text-xs">
                <thead className="bg-gray-50 dark:bg-gray-900/60 text-gray-500 dark:text-gray-400">
                  <tr>
                    <th className="text-left font-semibold px-3 py-2">Capacidad</th>
                    {matriz.roles.map((ro) => <th key={ro.rol} className="text-center font-semibold px-3 py-2">{ro.nombre}</th>)}
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                  {matriz.permisos.map((pe) => (
                    <tr key={pe.permiso} className="text-gray-700 dark:text-gray-300">
                      <td className="px-3 py-2">{pe.descripcion}</td>
                      {matriz.roles.map((ro) => <td key={ro.rol} className="px-3 py-2 text-center">{ro.permisos.includes(pe.permiso) ? <span className="text-green-600 font-bold">✓</span> : <span className="text-gray-300">—</span>}</td>)}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
  );
  const seccionEmpresasTracking = (app, r) => {
    const empresas = catalogo.empresas.filter((emp) => EMPRESAS_TRACKING.includes(String(emp.codigo).toUpperCase()));
    const matriz = matrices[app.codigo];
    const capacidades = (clave) => {
      const ro = matriz?.roles?.find((x) => x.rol === clave);
      if (!ro) return CAPACIDADES_POR_DEFECTO[clave];
      return ro.permisos.map((c) => matriz.permisos.find((pe) => pe.permiso === c)?.descripcion || c).join(' · ');
    };
    return (
      <>
        <p className="mb-3 text-xs text-gray-500 dark:text-gray-400">
          Marca, bajo cada perfil, las empresas en las que la persona lo tiene. En cada empresa tiene un solo perfil; marcarla bajo otro perfil se lo cambia.
          {r.tieneGeneral && !r.empresasTracking.length && ' Hoy tiene un acceso general antiguo: al marcar una empresa se reemplaza por el acceso por empresa.'}
        </p>
        <div className="space-y-3">
          {PERFILES_TRACKING.map((perfil) => (
            <div key={perfil.clave} className="rounded-xl border border-gray-200 dark:border-gray-700 p-4 bg-white dark:bg-gray-800">
              <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
                <div className="min-w-0 md:max-w-md">
                  <p className="text-sm font-bold text-gray-900 dark:text-white">{perfil.etiqueta}</p>
                  <p className="text-xs text-gray-500 dark:text-gray-400">{capacidades(perfil.clave)}</p>
                </div>
                <div className="flex flex-wrap gap-2">
                  {empresas.map((emp) => {
                    const fila = getPermisoEmpresa(app.id, emp.id);
                    const marcado = fila?.scope === perfil.clave;
                    const conOtro = fila && !marcado;
                    return (
                      <button
                        key={emp.id}
                        type="button"
                        disabled={saving}
                        aria-pressed={marcado}
                        title={conOtro ? `Hoy tiene ${etiquetaPerfil(fila.scope)} en ${emp.nombre}; al marcarla pasa a ${perfil.etiqueta}` : emp.nombre}
                        onClick={() => asignarPerfilEmpresa(app, emp, perfil.clave)}
                        className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border text-xs font-bold transition-colors disabled:opacity-50 ${
                          marcado
                            ? 'bg-blue-600 border-blue-600 text-white'
                            : conOtro
                              ? 'bg-gray-50 dark:bg-gray-900 border-gray-200 dark:border-gray-700 text-gray-400'
                              : 'bg-white dark:bg-gray-800 border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700'
                        }`}
                      >
                        {marcado && <Check className="w-3.5 h-3.5" />}
                        {emp.codigo}
                        {conOtro && <span className="font-normal text-[10px]">({etiquetaPerfil(fila.scope)})</span>}
                      </button>
                    );
                  })}
                </div>
              </div>
            </div>
          ))}
        </div>
        {tablaCapacidades(matriz)}
      </>
    );
  };
  const acordeon = (id, Icono, titulo, resumen, render) => (
    <section className="rounded-xl border border-gray-200 dark:border-gray-700 overflow-hidden">
      <button
        type="button"
        onClick={() => setSeccion(seccion === id ? null : id)}
        aria-expanded={seccion === id}
        className="w-full flex items-center justify-between gap-3 px-4 py-3 bg-gray-50 dark:bg-gray-900/40 hover:bg-gray-100 dark:hover:bg-gray-900/70 text-left"
      >
        <span className="flex items-center gap-2 text-sm font-semibold text-gray-800 dark:text-gray-200">
          <Icono className="w-4 h-4 text-blue-500" />{titulo}
        </span>
        <span className="flex items-center gap-2 text-xs text-gray-500 dark:text-gray-400">
          {resumen}
          {seccion === id ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
        </span>
      </button>
      {seccion === id && <div className="p-4 border-t border-gray-200 dark:border-gray-700">{render()}</div>}
    </section>
  );


  return (
    <div className="space-y-6">
      {/* Banner de Estado General */}
      <div className={`p-4 rounded-xl border flex items-center justify-between ${
        isActive
          ? 'bg-green-50 dark:bg-green-950/30 border-green-200 dark:border-green-800 text-green-800 dark:text-green-300'
          : 'bg-red-50 dark:bg-red-950/30 border-red-200 dark:border-red-800 text-red-800 dark:text-red-300'
      }`}>
        <div className="flex items-center space-x-3">
          <Shield className="w-5 h-5 flex-shrink-0" />
          <div>
            <h4 className="font-semibold text-sm">
              {isActive ? 'Cuenta Activa (Acceso a Servicios Habilitado)' : 'Cuenta Desvinculada (Kill Switch Inmediato)'}
            </h4>
            <p className="text-xs opacity-90">
              {isActive
                ? 'El usuario puede iniciar sesión en las aplicaciones donde tenga permisos asignados.'
                : 'Al estar desvinculado, se corta el acceso a TODAS las aplicaciones inmediatamente.'}
            </p>
          </div>
        </div>
        <span className={`px-2.5 py-1 rounded-full text-xs font-bold uppercase tracking-wider ${
          isActive ? 'bg-green-200 dark:bg-green-800 text-green-900 dark:text-green-100' : 'bg-red-200 dark:bg-red-800 text-red-900 dark:text-red-100'
        }`}>
          {isActive ? 'Activo' : 'Bloqueado'}
        </span>
      </div>

      {/* Cuadrícula de aplicaciones: cada tarjeta resume el estado; el detalle se abre en una ventana */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-4">
        {catalogo.apps.map((app) => {
          const r = resumenApp(app);
          return (
            <div
              key={app.id}
              role="button"
              tabIndex={0}
              onClick={() => { setSeccion(null); setAppDetalle(app.id); }}
              onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setSeccion(null); setAppDetalle(app.id); } }}
              className={`cursor-pointer text-left flex flex-col bg-white dark:bg-gray-800 rounded-xl border p-5 shadow-sm hover:shadow-md transition-all focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 ${
                r.tieneAlgun ? 'border-blue-300 dark:border-blue-700' : 'border-gray-200 dark:border-gray-700'
              }`}
            >
              <div className="flex items-start justify-between gap-3">
                <LogoApp codigo={app.codigo} activa={r.tieneAlgun} />
                <div className="flex flex-col items-end gap-2">
                  {r.esTracking ? (
                    <Interruptor activo={r.empresasTracking.length > 0 || r.tieneGeneral} disabled={saving} onChange={() => toggleAccesoTracking(app)} etiqueta={`Acceso a ${app.nombre}`} />
                  ) : (!r.esAutogestion || r.tieneGeneral) && (
                    <Interruptor activo={r.tieneGeneral} disabled={saving} onChange={() => toggleAccesoGeneralApp(app.id)} etiqueta={`Acceso total a ${app.nombre}`} />
                  )}
                  <span className={`px-2.5 py-0.5 rounded-full text-xs font-bold whitespace-nowrap ${claseAcceso(r)}`}>{r.etiquetaAcceso}</span>
                </div>
              </div>
              <h3 className="mt-3 text-base font-bold text-gray-900 dark:text-white">{app.nombre}</h3>
              <p className="mt-1 text-sm text-gray-500 dark:text-gray-400 line-clamp-2 min-h-[2.5rem]">{app.descripcion || 'Aplicación integrada del ecosistema'}</p>
              <dl className="mt-4 pt-3 border-t border-gray-100 dark:border-gray-700 space-y-1.5 text-xs">
                {r.datos.map(([k, v]) => (
                  <div key={k} className="flex items-center justify-between gap-3">
                    <dt className="text-gray-500 dark:text-gray-400">{k}</dt>
                    <dd className="font-semibold text-gray-800 dark:text-gray-200 text-right">{v}</dd>
                  </div>
                ))}
              </dl>
              <span className="mt-4 text-xs font-semibold text-blue-600 dark:text-blue-400">Ver detalle →</span>
            </div>
          );
        })}
      </div>

      {/* Detalle de la aplicación */}
      {appActual && (() => {
        const app = appActual;
        const r = resumenApp(app);
        const cuentas = cuentasPorApp[app.codigo];
        const combinaciones = permisos.filter((p) => p.appId === app.id && p.activo && p.empresaId && p.clienteId);
        return (
          <Modal logo={<LogoApp codigo={app.codigo} activa={r.tieneAlgun} />} titulo={app.nombre} subtitulo={app.descripcion || 'Aplicación integrada del ecosistema'} onClose={() => setAppDetalle(null)} esc={!editor} ancho="max-w-5xl">
            <div className="space-y-6">
              <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-3 text-sm">
                {app.urlCallback && (
                  <div className="sm:col-span-2">
                    <dt className="text-xs uppercase tracking-wide text-gray-400">Dirección de acceso</dt>
                    <dd className="font-mono text-xs text-gray-700 dark:text-gray-300 break-all">{app.urlCallback}</dd>
                  </div>
                )}
                <div>
                  <dt className="text-xs uppercase tracking-wide text-gray-400">Verificación en dos pasos</dt>
                  <dd className="text-gray-800 dark:text-gray-200">{r.usaCorreoDoble ? 'Código por correo' : (app.metodoSegundoFactor || '').toLowerCase() === 'totp' ? 'App autenticadora (TOTP)' : 'Según la identidad central'}</dd>
                </div>
                <div>
                  <dt className="text-xs uppercase tracking-wide text-gray-400 mb-1">Acceso a la aplicación</dt>
                  <dd className="flex items-center gap-3">
                    {r.esTracking ? (
                      <Interruptor activo={r.empresasTracking.length > 0 || r.tieneGeneral} disabled={saving} onChange={() => toggleAccesoTracking(app)} etiqueta={`Acceso a ${app.nombre}`} />
                    ) : (!r.esAutogestion || r.tieneGeneral) && (
                      <Interruptor activo={r.tieneGeneral} disabled={saving} onChange={() => toggleAccesoGeneralApp(app.id)} etiqueta={`Acceso total a ${app.nombre}`} />
                    )}
                    <span className={`px-2.5 py-0.5 rounded-full text-xs font-bold ${claseAcceso(r)}`}>{r.etiquetaAcceso}</span>
                  </dd>
                  {r.esAutogestion && !r.tieneGeneral && <p className="text-xs text-gray-500 mt-1">El acceso se da por empresa y cliente, en la sección de abajo.</p>}
                  {r.esTracking && <p className="text-xs text-gray-500 mt-1">{r.empresasTracking.length > 0 ? 'El acceso y el perfil se dan por empresa, en la sección de abajo.' : 'Al activarlo se da acceso en la empresa de la persona con perfil Staff; el resto se ajusta abajo.'}</p>}
                </div>
                {r.perfilTexto && (
                  <div>
                    <dt className="text-xs uppercase tracking-wide text-gray-400">Perfil</dt>
                    <dd className="text-gray-800 dark:text-gray-200">{r.perfilTexto}</dd>
                  </div>
                )}
              </dl>

              <div className="space-y-3">
                {r.tienePerfiles && acordeon('acceso', Pencil, 'Perfil', r.perfilTexto || 'Sin perfil', () => seccionAcceso(app, r))}
                {r.tienePermisosFinos && acordeon('finos', SlidersHorizontal, 'Rol y permisos', 'Configurables', () => (
                  <PermisosFinosApp userId={userId} appCodigo={app.codigo} appNombre={app.nombre} tieneAcceso={r.tieneAlgun} />
                ))}
                {r.esTracking && acordeon('empresas-tracking', Building, 'Empresas y perfil', r.empresasTracking.length ? r.empresasTracking.map((p) => p.empresaCodigo).filter(Boolean).join(' · ') : r.tieneGeneral ? 'Todas las de su ficha' : 'Sin acceso', () => seccionEmpresasTracking(app, r))}
                {r.esAutogestion && acordeon('empresas', Building, 'Empresas y clientes', r.tieneGeneral ? 'Todos' : `${combinaciones.length} asignadas`, () => seccionEmpresas(app, r))}
              </div>

              {r.esExportaciones && (
                <section>
                  <div className="flex items-center justify-between gap-3 mb-2">
                    <h4 className={`${tituloSeccion} mb-0`}><Users className="w-4 h-4 mr-1.5 text-blue-500" />{r.usaCorreoDoble ? 'Cuentas locales (correo y contraseña)' : 'Usuarios locales (usuario y contraseña)'}</h4>
                    <button
                      type="button"
                      onClick={() => {
                        setFormCrear({ nombre: userNombre || '', apellido: userApellido || '', usuario: userUsername || '', password: generarPasswordTemporal(), scope: 'usuario', mostrarPassword: true });
                        setEditor({ tipo: 'nuevaCuenta', appCodigo: app.codigo });
                      }}
                      className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-bold rounded-lg bg-blue-50 hover:bg-blue-100 dark:bg-blue-950/40 dark:hover:bg-blue-900/50 text-blue-600 dark:text-blue-400 border border-blue-200 dark:border-blue-800"
                    >
                      <Plus className="w-4 h-4" /><span>Nueva cuenta local</span>
                    </button>
                  </div>
                  <p className="text-xs text-gray-500 dark:text-gray-400 mb-3">
                    {r.usaCorreoDoble
                      ? 'Inician sesión en el login central con su correo y contraseña; el código de verificación llega a ese mismo correo. Son independientes de la nómina.'
                      : 'Para personas sin correo corporativo: usuario y contraseña en el login central, con verificación en dos pasos obligatoria. Son independientes de la nómina.'}
                  </p>
                  {loadingCuentas ? (
                    <p className="text-xs text-gray-400 italic">Cargando…</p>
                  ) : !cuentas || cuentas.length === 0 ? (
                    <p className="text-sm text-gray-400 italic">Todavía no hay cuentas locales creadas.</p>
                  ) : (
                    <div className="rounded-lg border border-gray-200 dark:border-gray-700 overflow-x-auto">
                      <table className="w-full text-xs">
                        <thead className="bg-gray-50 dark:bg-gray-900/60 text-gray-500 dark:text-gray-400">
                          <tr>
                            <th className="text-left font-semibold px-3 py-2">Nombre</th>
                            <th className="text-left font-semibold px-3 py-2">{r.usaCorreoDoble ? 'Correo' : 'Usuario'}</th>
                            <th className="text-left font-semibold px-3 py-2">Perfil</th>
                            <th className="text-left font-semibold px-3 py-2">Estado</th>
                            <th className="text-left font-semibold px-3 py-2">MFA</th>
                            <th className="px-3 py-2" />
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                          {cuentas.map((c) => (
                            <tr key={c.id} className="text-gray-700 dark:text-gray-300">
                              <td className="px-3 py-2 font-medium text-gray-900 dark:text-white">{c.nombre} {c.apellido}</td>
                              <td className="px-3 py-2 font-mono">{c.usuario}</td>
                              <td className="px-3 py-2">{c.scope ? etiquetaPerfil(c.scope) : '—'}</td>
                              <td className="px-3 py-2">{c.activo ? <span className="text-green-600 dark:text-green-400 font-medium">Activa</span> : <span className="text-red-600 dark:text-red-400 font-medium">Desactivada</span>}</td>
                              <td className="px-3 py-2">{c.totpEnabled ? 'Sí' : 'Pendiente'}</td>
                              <td className="px-3 py-2 text-right">
                                <button
                                  type="button"
                                  onClick={() => { setResetPassword({ password: generarPasswordTemporal(), mostrarPassword: true }); setConfirmarEliminar(false); setEditor({ tipo: 'cuenta', appCodigo: app.codigo, cuentaId: c.id }); }}
                                  className="px-2.5 py-1 text-[11px] font-semibold rounded-lg bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-300"
                                >
                                  Gestionar
                                </button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </section>
              )}
            </div>
          </Modal>
        );
      })()}

      {/* Ventanas de edición */}
      {editor && (() => {
        const app = catalogo.apps.find((a) => a.id === editor.appId || a.codigo === editor.appCodigo);
        if (!app) return null;
        const r = resumenApp(app);
        const cerrar = () => setEditor(null);

        if (editor.tipo === 'nuevaCuenta') {
          return (
            <Modal titulo={`Nueva cuenta local en ${app.nombre}`} subtitulo="Puedes crearla para cualquier persona; no tiene por qué ser la que estás viendo." onClose={cerrar} ancho="max-w-xl" z={9100}>
              <div className="space-y-4">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <label className="block"><span className={etiquetaCampo}>Nombre</span>
                    <input type="text" value={formCrear.nombre} onChange={(e) => setFormCrear((p) => ({ ...p, nombre: e.target.value }))} className={campo} /></label>
                  <label className="block"><span className={etiquetaCampo}>Apellido</span>
                    <input type="text" value={formCrear.apellido} onChange={(e) => setFormCrear((p) => ({ ...p, apellido: e.target.value }))} className={campo} /></label>
                  <label className="block sm:col-span-2"><span className={etiquetaCampo}>{r.usaCorreoDoble ? 'Correo (recibe el código del doble factor)' : 'Usuario'}</span>
                    <input type={r.usaCorreoDoble ? 'email' : 'text'} placeholder={r.usaCorreoDoble ? 'persona@empresa.cl' : 'nombre.apellido'} value={formCrear.usuario} onChange={(e) => setFormCrear((p) => ({ ...p, usuario: e.target.value }))} className={`${campo} font-mono`} />
                    <span className="text-[11px] text-gray-400">{r.usaCorreoDoble ? 'Inicia sesión con este correo y la contraseña; el código llega a este mismo correo.' : 'Solo letras, números, puntos y guiones. No hace falta un correo real.'}</span></label>
                  <div className="relative sm:col-span-2">
                    <span className={etiquetaCampo}>Contraseña (generada, se puede editar)</span>
                    <input type={formCrear.mostrarPassword ? 'text' : 'password'} value={formCrear.password} onChange={(e) => setFormCrear((p) => ({ ...p, password: e.target.value }))} className={`${campo} pr-9 font-mono`} />
                    <button type="button" onClick={() => setFormCrear((p) => ({ ...p, mostrarPassword: !p.mostrarPassword }))} className="absolute right-2.5 top-[30px] text-gray-400 hover:text-gray-600">
                      {formCrear.mostrarPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                  <div className="sm:col-span-2">
                    <span className={etiquetaCampo}>Perfil</span>
                    <div className="inline-flex items-center rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
                      {r.perfiles.slice().reverse().map((perfil, i) => (
                        <button key={perfil} type="button" onClick={() => setFormCrear((p) => ({ ...p, scope: perfil }))} className={`px-4 py-1.5 text-xs font-bold ${i > 0 ? 'border-l border-gray-200 dark:border-gray-700' : ''} ${formCrear.scope === perfil ? 'bg-blue-600 text-white' : 'bg-gray-50 dark:bg-gray-900 text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800'}`}>
                          {etiquetaPerfil(perfil)}
                        </button>
                      ))}
                    </div>
                  </div>
                </div>
                <div className="flex justify-end gap-2">
                  <button type="button" onClick={cerrar} className={botonSecundario}>Cancelar</button>
                  <button type="button" disabled={savingCuenta} onClick={() => handleCrearCuentaLocal(app.codigo)} className="px-4 py-2 text-sm font-bold rounded-lg bg-blue-600 hover:bg-blue-700 text-white disabled:opacity-50">Crear cuenta</button>
                </div>
              </div>
            </Modal>
          );
        }

        if (editor.tipo === 'cuenta') {
          const c = (cuentasPorApp[app.codigo] || []).find((x) => x.id === editor.cuentaId);
          if (!c) return null;
          return (
            <Modal titulo={`${c.nombre} ${c.apellido}`} subtitulo={`Cuenta local de ${app.nombre} · ${c.usuario}`} onClose={cerrar} ancho="max-w-lg" z={9100}>
              <div className="space-y-5">
                <div className="flex items-center justify-between gap-3 text-sm">
                  <span className="text-gray-600 dark:text-gray-300">Estado</span>
                  <button
                    type="button"
                    disabled={savingCuenta}
                    onClick={() => handleToggleActivoCuenta(app.codigo, c.id, c.activo)}
                    className={`px-3 py-1.5 text-xs font-semibold rounded-lg ${c.activo ? 'bg-red-50 hover:bg-red-100 dark:bg-red-950/40 text-red-600 dark:text-red-400' : 'bg-green-50 hover:bg-green-100 dark:bg-green-950/40 text-green-600 dark:text-green-400'}`}
                  >
                    {c.activo ? 'Activa · Desactivar' : 'Desactivada · Activar'}
                  </button>
                </div>

                <div>
                  <span className={etiquetaCampo}>Nueva contraseña (generada, se puede editar)</span>
                  <div className="flex gap-2">
                    <div className="relative flex-1">
                      <input type={resetPassword.mostrarPassword ? 'text' : 'password'} value={resetPassword.password} onChange={(e) => setResetPassword((p) => ({ ...p, password: e.target.value }))} className={`${campo} pr-9 font-mono`} />
                      <button type="button" onClick={() => setResetPassword((p) => ({ ...p, mostrarPassword: !p.mostrarPassword }))} className="absolute right-2.5 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600">
                        {resetPassword.mostrarPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                      </button>
                    </div>
                    <button type="button" disabled={savingCuenta} onClick={() => handleResetPassword(app.codigo, c.id)} className="px-4 py-1.5 text-xs font-bold rounded-lg bg-blue-600 hover:bg-blue-700 text-white disabled:opacity-50">Restablecer</button>
                  </div>
                </div>

                <div className="pt-4 border-t border-gray-100 dark:border-gray-700">
                  {!confirmarEliminar ? (
                    <button type="button" onClick={() => setConfirmarEliminar(true)} className="px-3 py-1.5 text-xs font-semibold rounded-lg bg-red-600 hover:bg-red-700 text-white">Eliminar cuenta</button>
                  ) : (
                    <div className="rounded-lg bg-red-50 dark:bg-red-950/30 p-3 flex items-center justify-between gap-3 flex-wrap">
                      <p className="text-xs text-red-700 dark:text-red-300 font-medium">¿Eliminar la cuenta de {c.nombre} {c.apellido} ({c.usuario})? No se puede deshacer.</p>
                      <div className="flex gap-2">
                        <button type="button" disabled={savingCuenta} onClick={() => handleEliminarCuentaLocal(app.codigo, c.id)} className="px-3 py-1.5 text-xs font-bold rounded-lg bg-red-600 hover:bg-red-700 text-white disabled:opacity-50">Sí, eliminar</button>
                        <button type="button" onClick={() => setConfirmarEliminar(false)} className={botonSecundario}>Cancelar</button>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </Modal>
          );
        }
        return null;
      })()}
    </div>
  );
}
